package terraultima.web

import kotlinx.browser.window
import org.w3c.dom.url.URLSearchParams
import terraultima.Content
import terraultima.Fmt
import terraultima.game.DecisionView
import terraultima.game.OptionView
import terraultima.game.Severity
import kotlin.js.Promise
import kotlin.random.Random

/**
 * Four screens and one rule: a decision is SELECTED by tapping a card and COMMITTED by the button at
 * the bottom of the client.
 *
 * That rule is the whole interaction design. A century of consequences behind a single mis-tap is
 * cruel, and a confirmation dialog on every choice is worse — it asks "are you sure?" about the one
 * thing the game is entirely made of. Selecting first means the price of the option you are holding
 * stays on screen next to the stores it will come out of, and the commit lives where a Telegram app
 * always puts its irreversible action.
 */
object App {

    private var save: Save? = null
    private var result: RunResult? = null
    private var selected: Int? = null
    /** The option that has been asked about once because it costs lives. */
    private var armed: Int? = null
    /** Which tab is showing: decide, colony, world, history. */
    private var tab = "decide"
    /** Decisions answered since this app was opened, so the app can offer a place to stop. */
    private var sitting = 0
    private var anotherShipOpen = false
    /** Opened from the bot's keyboard, so the run belongs to the bot and goes back to it. */
    private var fromBot = false
    private var sheet: String? = null
    private var premisePick: Int? = null
    private var seedOverride: Int? = null
    private var digestOpen = false
    private var busy = false
    private var failure: String? = null
    private val openSections = mutableSetOf("budgets")
    /** This run came from a `?seed=…&choices=…` link rather than from storage. */
    private var fromLink = false
    /** How long the last replay took, in milliseconds. Shown in the designer view. */
    private var lastReplayMs = 0.0

    private val host get() = byId("app")!!

    // ------------------------------------------------------------------ boot

    fun boot() {
        Tg.boot()
        render()
        fetchCorpus().then({
            val q = URLSearchParams(window.location.search)
            fromBot = q.get("src") == "bot"
            val shared = fromUrl()
            val nonce = q.get("new")
            val seen = runCatching { window.localStorage.getItem("tu_new_seen") }.getOrNull()
            if (fromBot && nonce != null && nonce != seen) {
                // A new century from the bot's keyboard: whatever this device had is over. Once:
                // the same button reopened before the app has reported back must not wipe the
                // century the player has started since.
                runCatching { window.localStorage.setItem("tu_new_seen", nonce) }
                Store.clear(); save = null; render()
            } else if (fromBot && nonce != null) {
                Store.load { s -> save = s?.takeIf { !it.finished }; if (save != null) replay() else render() }
            } else if (shared != null && fromBot) {
                // The bot's record, unless this device got further in the same century and the
                // app was closed before it could report back.
                Store.load { local ->
                    save = if (local != null && local.seed == shared.seed && local.motive == shared.motive &&
                        local.choices.size > shared.choices.size &&
                        local.choices.take(shared.choices.size) == shared.choices) local else shared
                    Store.save(save!!)
                    replay()
                }
            } else if (shared != null) {
                // A link to an exact century. Not written to storage until the player actually
                // decides something — following someone's link should not silently eat their run.
                save = shared
                fromLink = true
                replay()
            } else Store.load { s ->
                save = s?.takeIf { !it.finished }
                if (save != null) replay() else render()
            }
        }, { err: Throwable ->
            failure = "The corpus did not load: ${err.message ?: err.toString()}"
            render()
        })
    }

    /**
     * The corpus travels with the page as six JSON files. `Content` used to read them off a disk,
     * which is exactly what a browser does not have — splitting that out (`ContentFiles.kt`) is what
     * let the engine compile to JavaScript at all.
     */
    private fun fetchCorpus(): Promise<Unit> {
        val files = Content.FILES
        val fetches = files.map { name ->
            window.fetch("content/$name").then { r ->
                if (!r.ok) throw RuntimeException("content/$name — HTTP ${r.status}")
                r.text()
            }.asDynamic() as Promise<String>
        }.toTypedArray()
        return Promise.all(fetches).then { texts ->
            Content.loadFrom(files.zip(texts.toList()).toMap())
        }
    }

    // ------------------------------------------------------------------ the loop

    /**
     * `?seed=481923&motive=0&choices=1,3,2` — a link to one exact century.
     *
     * The web counterpart of `./play script --seed=… --choices=…`, and the same property makes both
     * work: seed plus the numbers tapped IS the run. It means a cold read can be handed the precise
     * state a reader stopped at, and a bug report can be a URL.
     */
    private fun fromUrl(): Save? {
        val q = URLSearchParams(window.location.search)
        val seed = q.get("seed")?.trim()?.toIntOrNull() ?: return null
        fromBot = q.get("src") == "bot"
        val choices = (q.get("choices") ?: "").split(",")
            .mapNotNull { it.trim().toIntOrNull() }
        return Save(
            seed = seed,
            motive = q.get("motive")?.trim()?.toIntOrNull() ?: 0,
            choices = choices,
            launched = q.get("launched")?.let { it != "0" && it != "false" } ?: choices.isNotEmpty(),
            debug = q.get("debug").let { it != null && it != "0" && it != "false" },
            corpus = Content.corpus.events.size
        )
    }

    private fun replay() {
        val s = save ?: return
        val before = result?.decision
        val t0 = js("performance.now()") as Double
        result = Run.replay(s.seed, s.motive, s.choices, s.launched)
        lastReplayMs = (js("performance.now()") as Double) - t0
        selected = null
        armed = null
        // What happened since the last decision is the consequence of it: open, not folded.
        digestOpen = (result?.outcomes?.isNotEmpty() == true) || (result?.digest?.size ?: 0) in 1..12
        val now = result?.decision
        if (now != null && now !== before) {
            // A NEW SCREEN STARTS AT THE TOP. The page kept the scroll of the one before, so a
            // commit from the bottom of a long decision landed mid-way down the next question,
            // with "What came of it" (the landing went badly) scrolled away above the fold.
            window.scrollTo(0.0, 0.0)
            // A crisis should not arrive at the same volume as an opportunity.
            Tg.haptic(if (now.severity == Severity.CRISIS) "warning" else "light")
        }
        if (result?.ending != null) Tg.haptic("success")
        render()
    }

    private fun commit(n: Int) {
        val s = save ?: return
        Tg.haptic("medium")
        save = s.copy(choices = s.choices + n, corpus = Content.corpus.events.size)
        Store.save(save!!)
        sitting++
        armed = null
        fromLink = false
        // Paint the pending state BEFORE the replay: a hundred years of simulation is fast but not
        // free, and a button that goes dead for 300 ms with no acknowledgement reads as a bug.
        busy = true
        render()
        window.setTimeout({ busy = false; replay() }, 20)
    }

    private fun launch() {
        val s = save ?: return
        Tg.haptic("heavy")
        save = s.copy(launched = true)
        Store.save(save!!)
        busy = true
        render()
        window.setTimeout({ busy = false; replay() }, 20)
    }

    private fun begin(motive: Int) {
        val seed = seedOverride ?: Random.nextInt(1, 1_000_000)
        seedOverride = null
        save = Save(seed = seed, motive = motive, corpus = Content.corpus.events.size)
        Store.save(save!!)
        premisePick = null
        openSections.clear(); openSections += "budgets"
        busy = true
        render()
        window.setTimeout({ busy = false; replay() }, 20)
    }

    private fun abandon() {
        Tg.confirm("Abandon this colony? The century ends here and nothing is kept.") { ok ->
            if (!ok) return@confirm
            Store.clear()
            save = null; result = null; selected = null; sheet = null
            Tg.haptic("rigid")
            render()
        }
    }

    // ------------------------------------------------------------------ render

    private fun render() {
        val f = failure
        if (f != null) { renderFailure(f); return }
        if (!Content.loaded) { renderLoading(); return }
        val r = result
        val s = save
        when {
            s == null || r == null -> renderPremise()
            r.ending != null -> renderEnding()
            !s.launched && r.setup != null -> renderExpedition()
            r.decision != null -> renderDecision()
            else -> renderPremise()
        }
        renderSheet()
    }

    private fun renderLoading() {
        Tg.mainButton(null)
        paint(host) {
            div("screen center") {
                div("title-mark") { +"TERRA ULTIMA" }
                line("note", "loading a hundred years…")
            }
        }
    }

    private fun renderFailure(msg: String) {
        Tg.mainButton("reload") { window.location.reload() }
        paint(host) {
            div("screen center") {
                div("title-mark") { +"TERRA ULTIMA" }
                line("note warn", msg)
            }
        }
    }

    // ------------------------------------------------------------------ premise

    private fun renderPremise() {
        val motives = Content.corpus.catastrophes
        val pick = premisePick
        Tg.backButton(null)
        Tg.mainButton(if (pick == null) null else "assemble the expedition") {
            if (pick != null) begin(pick)
        }
        paint(host) {
            div("screen") {
                div("hero") {
                    div("title-mark") { +"TERRA ULTIMA" }
                    p("body") {
                        +("You are not a person. You are whatever authority this colony recognises — " +
                          "a council, a captain, a habit of obedience. You will make roughly one " +
                          "decision a year for a hundred years, and nobody will tell you which ones " +
                          "mattered.")
                    }
                    div("gear-row") {
                        button("ghost") { click { openSheet("settings") }; +"⚙ settings" }
                    }
                }
                div("panel-label") { +"WHY ARE YOU LEAVING EARTH?" }
                line("note", "This fixes the ship, the destination, and what the century is about.")
                motives.forEachIndexed { n, m ->
                    div("card premise" + if (pick == n) " selected" else "") {
                        click { premisePick = n; Tg.haptic("light"); render() }
                        div("card-top") {
                            span("badge") { +"${n + 1}" }
                            span("card-title") { +m.name }
                        }
                        p("body") { +m.line }
                        if (pick == n) {
                            p("body dim") { +m.detail }
                            div("teaches") { +m.teaches }
                        }
                    }
                }
                div("footer-note") {
                    +(if (Tg.inTelegram) "Your century is saved to Telegram, so it follows you between devices."
                      else "Opened outside Telegram: your century is saved in this browser only.")
                }
            }
        }
    }

    // ------------------------------------------------------------------ expedition

    private fun renderExpedition() {
        val r = result ?: return
        val v = r.setup ?: return
        val snap = r.snap ?: return
        Tg.backButton { abandon() }
        Tg.mainButton(if (busy) "…" else "launch") { if (!busy) launch() }
        paint(host) {
            div("screen") {
                div("topbar") {
                    div("topbar-main") { +"THE EXPEDITION" }
                    button("ghost small") { click { openSheet("settings") }; +"⚙" }
                }
                Panels.expedition(this, v, snap, { openSections.contains(it) }) {
                    if (!openSections.remove(it)) openSections += it
                    render()
                }
                div("footer-note") { +"Once you launch, the century runs on its own clock." }
            }
        }
    }

    // ------------------------------------------------------------------ the decision

    /**
     * THE DECIDE SCREEN, in the order a phone is read: where you are and only what this decision
     * touches; what the situation does whatever you choose; the question; the answers, each one a
     * line of argument and a row of costs. Everything else is one tab or one tap away.
     */
    private fun renderDecision() {
        val r = result ?: return
        val d = r.decision ?: return
        val snap = r.snap ?: return
        val sel = selected?.let { n -> d.shown.firstOrNull { it.number == n && it.available } }
        Tg.backButton(if (tab != "decide") ({ tab = "decide"; render() }) else null)
        Tg.mainButton(
            when {
                tab != "decide" -> null
                busy -> "…"
                sel == null -> null
                sel.lethal && armed != sel.number -> "Choose: ${sel.label}"
                sel.lethal -> "☠ Yes, ${sel.label.replaceFirstChar { it.lowercase() }}"
                else -> "Choose: ${sel.label}"
            },
            destructive = sel?.lethal == true && armed == sel.number
        ) {
            if (busy || sel == null) return@mainButton
            if (sel.lethal && armed != sel.number) {
                armed = sel.number; Tg.haptic("warning"); render()
            } else commit(sel.number)
        }

        paint(host) {
            div("screen with-tabs") {
                tabs(this, snap)
                when (tab) {
                    "colony" -> Panels.colony(this, snap, save?.debug == true)
                    "world" -> Panels.world(this, snap)
                    "history" -> Panels.log(this, snap)
                    else -> decide(this, r, d, snap, sel)
                }
            }
        }
    }

    private fun tabs(n: N, snap: Snapshot) {
        n.div("tabs") {
            for ((id, label) in listOf("decide" to "Decide", "colony" to "Colony",
                    "world" to "World", "history" to "History")) {
                if (id == "world" && snap.facts.isEmpty()) continue
                button("tabbtn" + if (tab == id) " on" else "") {
                    click { tab = id; Tg.haptic("soft"); render() }
                    +label
                }
            }
            button("tabbtn gear") { click { openSheet("settings") }; +"⚙" }
        }
    }

    private fun decide(n: N, r: RunResult, d: DecisionView, snap: Snapshot, sel: OptionView?) = n.apply {
        context(this, d, snap, sel)
        if (r.diverged > 0) div("verdict warn") {
            +("${r.diverged} of your ${save?.choices?.size ?: 0} decisions could not be replayed, " +
              "so the colony did something else there. It happens when the game's events have " +
              "changed since this century was played.")
        }
        if (r.digest.isNotEmpty() || r.outcomes.isNotEmpty()) meanwhile(this, r)

        div("event") {
            div("event-head") {
                if (d.severity == Severity.CRISIS || d.severity == Severity.OPPORTUNITY)
                    span("sev ${Panels.severityClass(d.severity)}") { +Panels.severityWord(d.severity) }
                span("since") { +("year ${d.year}" + (if (d.sinceLast.isNotEmpty()) " · ${d.sinceLast} since your last decision" else "")) }
            }
            div("event-title") { +d.title }
            if (d.previously.isNotEmpty()) div("previously") { +d.previously }
            for (para in d.body.split("\n").filter { it.isNotBlank() }) p("body") { +para.trim() }
        }
        if (d.stakes.isNotEmpty()) div("stakes") { +d.stakes }

        div("answers") {
            for (o in d.shown) answer(this, d, o, sel?.number == o.number)
        }
        if (d.alsoPossible.isNotEmpty()) div("fold" + if (anotherShipOpen) " open" else "") {
            div("fold-head") {
                click { anotherShipOpen = !anotherShipOpen; render() }
                +(if (d.alsoPossible.size == 1) "1 answer another ship could give"
                  else "${d.alsoPossible.size} answers another ship could give")
                span("chev") { +(if (anotherShipOpen) "⌃" else "›") }
            }
            if (anotherShipOpen) {
                for (o in d.alsoPossible) div("fold-item") { +o.label }
                div("fold-note") { +"This ship did not bring what they need. Nothing this century will change that." }
            }
        }
        if (sitting >= 5 && fromBot) div("stop-here") {
            div("stop-text") { +"A good place to stop. The colony waits for you." }
            button("ghost") { click { backToChat() }; +"Back to the chat" }
        }
        if (sel == null) div("hint-foot") { +"Tap an answer to see it in full." }
    }

    /**
     * Year, place, people, and then ONLY the gauges and stores this decision's answers move, with
     * where each stands now. The whole colony is one tab away; eleven values above every question
     * was the thing both reviewers called homework.
     */
    private fun context(n: N, d: DecisionView, snap: Snapshot, sel: OptionView?) {
        val c = snap.colony
        val touched = d.shown.flatMap { it.chips }.map { it.kind to it.text.substringBefore(" ") }.toSet()
        n.div("context") {
            div("ctx-top") {
                span("ctx-year") { +"Year ${c.year}" }
                span("ctx-place") { +c.place }
                span("ctx-people") { +"${c.souls} people" }
            }
            div("ctx-stats") {
                for (st in c.stocks) {
                    val key = st.label.lowercase()
                    if (st.low || touched.contains("stock" to key)) span("ctx-stat" + if (st.low) " warn" else "") {
                        span("k") { +key }; span("v") { +shortNote(st.note) }
                    }
                }
                for (g in c.gauges) {
                    val hit = touched.contains("gauge" to g.label.lowercase())
                    if (hit || g.band <= 0) span("ctx-stat" + if (g.band <= 1) " warn" else "") {
                        span("k") { +g.label.lowercase() }; span("v") { +g.word }
                    }
                }
                if (c.powerShort) span("ctx-stat warn") { span("k") { +"power" }; span("v") { +c.powerNote } }
            }
        }
    }

    /** "24 months' worth" -> "24 mo"; the full sentence lives on the Colony tab. */
    private fun shortNote(note: String): String = note.substringBefore(",").substringBefore(" · ")
        .replace(" months' worth", " mo").replace(" months of drinking if the loop stops", " mo")
        .replace(" years left", " y").replace(" months left", " mo").replace(" year left", " y")

    private fun answer(n: N, d: DecisionView, o: OptionView, isSelected: Boolean) {
        val locked = !o.available
        n.div("answer" + (if (isSelected) " selected" else "") + if (locked) " locked" else "") {
            click {
                if (locked) { Tg.haptic("error"); Tg.alert(lockReason(o)) }
                else if (isSelected) { selected = null; armed = null; render() }
                else { selected = o.number; armed = null; Tg.haptic("light"); render() }
            }
            div("a-label") { +o.label }
            if (locked) {
                div("a-lock") { +("🔒 " + lockLine(o)) }
                return@div
            }
            if (o.hint.isNotEmpty() && !isSelected) div("a-hint") { +o.hint }
            chips(this, o)
            if (isSelected) div("a-full") {
                if (o.hint.isNotEmpty()) p("body") { +o.hint }
                if (o.pays.isNotEmpty()) div("a-row") { span("a-k bad") { +"Costs" }; span { +o.pays.joinToString(" · ") } }
                if (o.gains.isNotEmpty()) div("a-row") { span("a-k good") { +"Gains" }; span { +o.gains.joinToString(" · ") } }
                if (o.pays.isEmpty() && o.gains.isEmpty() && o.priceNote.isNotEmpty())
                    div("a-row") { span("a-k") { +"Costs" }; span { +o.priceNote } }
                if (o.starts.isNotEmpty()) div("a-row") { span("a-k") { +"Begins" }; span { +o.starts } }
                if (o.learns.isNotEmpty()) div("a-row") { span("a-k") { +"Settles" }; span { +o.learns } }
                // THE PREVIEW MUST INCLUDE THE SITUATION. An option's own price is only its share;
                // the famine takes its share whatever is chosen, and a preview that shows one
                // without the other is how "1 life" came to read as the whole cost.
                if (d.stakes.isNotEmpty()) div("a-row") { span("a-k bad") { +"And" }; span { +(d.stakes.substringBefore(". ") + ".") } }
                if (o.takenBefore) div("a-row dim") { +"You chose this last time." }
                if (armed == o.number) div("a-armed") { +"This costs lives. Tap the button again to confirm." }
                if (save?.debug == true && o.debug.isNotEmpty())
                    div("debug") { for (l in o.debug) div("debug-line") { +l } }
            }
        }
    }

    /** The price as chips: costs first, then gains, each with its size in dots. */
    private fun chips(n: N, o: OptionView) {
        val all = o.chips.sortedBy { if (it.good) 1 else 0 }
        if (all.isEmpty()) {
            n.div("chips") { span("chip none") { +(o.priceNote.ifEmpty { "nothing you can measure" }) } }
            return
        }
        n.div("chips") {
            for (c in all) span("chip " + (if (c.good) "good" else "bad") + if (c.kind == "lives") " lives" else "") {
                +((if (c.kind == "lives") "☠ " else if (c.good) "✚ " else "✖ ") + c.text +
                        (if (c.dots > 0) " " + "●".repeat(c.dots) else ""))
            }
        }
        // A gamble shows its branches, not the word: the bad branch is often the whole cost.
        if (o.odds.isNotEmpty()) n.div("odds") {
            for (line in o.odds) div("odds-line") { span("chip gamble") { +("🎲 " + line.substringBefore(": ")) }; +(" " + line.substringAfter(": ")) }
        }
    }

    private fun lockLine(o: OptionView): String = when {
        o.whyNot.isNotEmpty() -> o.whyNot
        o.needs.isNotEmpty() -> "you cannot " + o.needs.map { it.substringBefore(" — that takes ") }.joinToString(" or ")
        else -> "not available now"
    }

    /** End the sitting: the run goes back to the bot, which posts where the colony stands. */
    private fun backToChat() {
        val s = save ?: return
        val payload = """{"seed":${s.seed},"motive":${s.motive},"launched":${s.launched},"choices":[${s.choices.joinToString(",")}]}"""
        Tg.haptic("success")
        if (!Tg.sendData(payload)) Tg.close()
        window.setTimeout({ Tg.close() }, 600)
    }

    private fun meanwhile(n: N, r: RunResult) {
        val years = r.digest.count { it.startsWith("year ") }
        n.div("meanwhile" + if (digestOpen) " open" else "") {
            div("meanwhile-head") {
                click { digestOpen = !digestOpen; render() }
                span("meanwhile-title") {
                    +when {
                        years > 1 -> "Since your last decision: $years years"
                        r.outcomes.isNotEmpty() -> "What came of it"
                        else -> "Since your last decision"
                    }
                }
                span("section-chevron") { +(if (digestOpen) "⌃" else "⌄") }
            }
            if (digestOpen) div("meanwhile-body") {
                for (o in r.outcomes) div("outcome") { +o }
                for (l in r.digest) {
                    if (l.startsWith("year ")) div("digest-year") { +l }
                    else div("digest-note") { +l }
                }
            } else if (r.outcomes.isNotEmpty()) {
                div("meanwhile-peek") { +r.outcomes.first() }
            }
        }
    }

    /** The full reason, which is two facts: what you cannot do, and what it would have taken. */
    private fun lockReason(o: OptionView): String = when {
        o.needs.isNotEmpty() -> {
            val verbs = o.needs.map { it.substringBefore(" — that takes ") }.joinToString(" or ")
            val kit = o.needs.mapNotNull {
                it.substringAfter(" — that takes ", "").ifEmpty { null }
            }.distinct().joinToString("; ")
            "You cannot $verbs." + (if (kit.isEmpty()) "" else "\n\nThat takes: $kit.") +
                    "\n\nEquipment you did not bring. It could still be acquired."
        }
        o.whyNot.isNotEmpty() -> "${o.whyNot}\n\nYou lack no equipment; the situation does not call for it."
        else -> "Not available in this situation."
    }

    // ------------------------------------------------------------------ the ending

    private fun renderEnding() {
        val r = result ?: return
        val e = r.ending ?: return
        val snap = r.snap
        Tg.backButton(null)
        if (fromBot) Tg.mainButton("Post the ending to the chat") { backToChat() }
        else Tg.mainButton("another century") {
            Store.clear(); save = null; result = null; premisePick = null; render()
        }
        val ev = e.evidence.toMap()
        paint(host) {
            div("screen") {
                div("ending-banner ${e.tier.lowercase()}") {
                    div("ending-tier") { +e.tier }
                    div("ending-note") { +e.note }
                }
                div("facts-grid") {
                    endStat(this, "year", e.year.toString())
                    endStat(this, "people", e.people.toString())
                    endStat(this, "decisions", e.decisions.toString())
                }
                if (!e.alive) div("verdict warn") { +"The colony ended: ${e.endReason}" }
                if (r.digest.isNotEmpty() || r.outcomes.isNotEmpty()) {
                    div("panel-label") { +"THE LAST OF IT" }
                    for (o in r.outcomes) div("outcome") { +o }
                    for (l in r.digest) div("digest-note") { +l }
                }
                if (e.story.isNotEmpty()) {
                    div("panel-label") { +"WHAT HAPPENED" }
                    for (s in e.story) p("body") { +s }
                }
                if (e.identity.isNotEmpty()) {
                    div("panel-label") { +"WHAT THIS COLONY BECAME" }
                    for ((strength, word) in e.identity) div("identity") {
                        div("identity-top") {
                            span("identity-strength") { +strength }
                            span("identity-word") { +word }
                        }
                        ev[word]?.let { div("readout-note") { +it } }
                    }
                    line("note", "Nobody chose those words. They are what you kept deciding.")
                }
                if (snap != null) {
                    div("panel-label") { +"EVERY DECISION YOU MADE" }
                    Panels.log(this, snap)
                }
                div("gear-row") {
                    button("ghost") { click { openSheet("settings") }; +"⚙ seed & replay" }
                }
            }
        }
    }

    private fun endStat(n: N, label: String, value: String) {
        n.div("fact-cell") {
            div("panel-label") { +label.uppercase() }
            div("fact-value") { +value }
        }
    }

    // ------------------------------------------------------------------ sheets

    private fun openSheet(id: String) {
        sheet = id
        Tg.haptic("soft")
        Tg.backButton { closeSheet() }
        renderSheet()
    }

    private fun closeSheet() {
        sheet = null
        Tg.backButton(null)
        render()
    }

    private fun renderSheet() {
        val existing = byId("sheet")
        existing?.remove()
        val id = sheet ?: return
        val snap = result?.snap
        val holder = kotlinx.browser.document.createElement("div") as org.w3c.dom.HTMLElement
        holder.id = "sheet"
        holder.className = "sheet-wrap"
        kotlinx.browser.document.body?.appendChild(holder)
        paint(holder) {
            div("backdrop") { click { closeSheet() } }
            div("sheet") {
                div("sheet-grip") { click { closeSheet() } }
                div("sheet-head") {
                    span("sheet-title") {
                        +when (id) {
                            "colony" -> "The colony"
                            "world" -> "The world"
                            "aims" -> "What you are working toward"
                            "log" -> "What you decided"
                            else -> "Settings"
                        }
                    }
                    button("ghost small") { click { closeSheet() }; +"✕" }
                }
                div("sheet-body") {
                    when {
                        id == "settings" -> settingsSheet(this)
                        snap == null -> line("note", "nothing to show yet.")
                        id == "colony" -> Panels.colony(this, snap, save?.debug == true)
                        id == "world" -> Panels.world(this, snap)
                        id == "aims" -> Panels.aims(this, snap)
                        id == "log" -> Panels.log(this, snap)
                    }
                }
            }
        }
    }

    private fun settingsSheet(n: N) {
        val s = save
        n.apply {
            div("panel-label") { +"DESIGNER VIEW" }
            div("toggle-row") {
                click {
                    val cur = save ?: return@click
                    save = cur.copy(debug = !cur.debug)
                    Store.save(save!!)
                    render(); renderSheet()
                }
                div("toggle-body") {
                    div("readout-name") { +"show the machinery" }
                    div("readout-note") {
                        +("Event ids, why this event arrived this month, the hidden pressures, and " +
                          "what each option actually does. It spoils the game on purpose.")
                    }
                }
                span("switch" + if (s?.debug == true) " on" else "") { +"" }
            }

            if (s != null) {
                div("panel-label") { +"THIS RUN" }
                div("readout") {
                    div("readout-body") {
                        div("readout-top") {
                            span("readout-name") { +"seed" }
                            span("readout-value mono") { +s.seed.toString() }
                        }
                        div("readout-note") {
                            +"The same seed and the same taps make the same century."
                        }
                    }
                }
                val cmd = "./play script --seed=${s.seed} --motive=${s.motive}" +
                        (if (s.choices.isEmpty()) "" else " --choices=" + s.choices.joinToString(","))
                div("code-block") {
                    div("code") { +cmd }
                    button("ghost small") {
                        click {
                            runCatching { window.navigator.asDynamic().clipboard.writeText(cmd) }
                            Tg.haptic("soft")
                        }
                        +"copy"
                    }
                }
                line("note", "Reproduces this run in a terminal — for a bug report, or to hand " +
                        "someone the exact century you played.")
                if (s.corpus != 0 && s.corpus != Content.corpus.events.size) line("note warn",
                    "This run was played against ${s.corpus} events; there are now " +
                            "${Content.corpus.events.size}. It will still resume, but a choice may " +
                            "land on an option that has since moved.")
            } else {
                div("panel-label") { +"NEXT RUN" }
                div("readout") {
                    div("readout-body") {
                        div("readout-top") {
                            span("readout-name") { +"seed" }
                            span("readout-value mono") { +(seedOverride?.toString() ?: "random") }
                        }
                        div("readout-note") { +"Set one to replay a century somebody else played." }
                    }
                }
                div("code-block") {
                    val input = tag("input", "seed-input")
                    input.setAttribute("type", "number")
                    input.setAttribute("placeholder", "42")
                    button("ghost small") {
                        click {
                            seedOverride = (input.asDynamic().value as? String)?.trim()?.toIntOrNull()
                            Tg.haptic("soft")
                            renderSheet()
                        }
                        +"set"
                    }
                }
            }

            if (s?.debug == true) line("note mono",
                "last replay: ${Fmt.dec(lastReplayMs, 1)} ms for ${s.choices.size} decisions")
            if (fromLink) line("note warn",
                "This century came from a link. It is not saved yet — the first decision you make " +
                        "here replaces whatever run this device had.")

            div("panel-label") { +"THE GAME" }
            line("note", "${Content.corpus.events.size} events · ${Content.corpus.builds.size} ships · " +
                    "${Content.corpus.planets.size} worlds · ${Content.corpus.catastrophes.size} premises")
            line("note", if (Tg.inTelegram)
                "Saved to Telegram's cloud storage, so this century follows you between devices."
            else "Saved in this browser only — open it inside Telegram to keep it across devices.")

            if (s != null) {
                div("danger-row") {
                    button("danger") { click { closeSheet(); abandon() }; +"abandon this century" }
                }
            }
        }
    }
}

/**
 * One entry point, two environments. In a browser this is the game; under Node there is no
 * `document`, and the same bundle runs [SelfCheck] instead — which is how the JavaScript build of
 * the engine gets verified without a browser.
 */
fun main() {
    if (js("typeof document === 'undefined'") as Boolean) SelfCheck.run() else App.boot()
}
